import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { SnackbarService } from '@ccfx/shared-lib';
import { TranslocoService } from '@jsverse/transloco';
import { KycReviewStage, KycReviewStatusDto } from '../models/onboarding-models';
import { OnboardingApiService } from './onboarding-api.service';
import { OnboardingStatusService } from './onboarding-status.service';

/** Day thresholds (FR-7.1–7.3) at which each recurring KYC re-review stage begins. */
const UPCOMING_THRESHOLD_DAYS = 42; // 6 weeks
const DUE_THRESHOLD_DAYS = 30;
const WARNING_THRESHOLD_DAYS = 14; // 2 weeks

/** Maps the backend `kycReviewStatus` value to its corresponding re-review lifecycle stage. */
const REVIEW_STATUS_STAGE: Record<string, Exclude<KycReviewStage, null>> = {
  InProgress: 'reReviewDraft',
  PendingReview: 'reReviewPendingReview',
  RevisionsRequested: 'reReviewChangesRequested',
};

/**
 * Fetches the company's recurring KYC re-review status (FR-7.1–7.6) and derives the
 * current lifecycle stage. Single source of truth for both the persistent portal
 * banner and the in-app notification bell.
 */
@Injectable({ providedIn: 'root' })
export class KycReviewStatusService {
  private readonly api = inject(OnboardingApiService);
  private readonly onboardingStatus = inject(OnboardingStatusService);
  private readonly snackbar = inject(SnackbarService);
  private readonly transloco = inject(TranslocoService);

  private readonly _statusDto = signal<KycReviewStatusDto | null>(null);
  private readonly _statusLoaded = signal(false);
  /** Compliance's revision notes for the active re-review draft — only set while RevisionsRequested. */
  private readonly _reviewNotes = signal<string | null>(null);
  private hasLoaded = false;

  readonly daysUntilDue = computed(() => this._statusDto()?.daysUntilDue ?? null);
  readonly kycReReviewDate = computed(() => this._statusDto()?.kycReReviewDate ?? null);
  readonly canStartReReview = computed(() => this._statusDto()?.canStartReReview ?? false);
  readonly statusLoaded = this._statusLoaded.asReadonly();
  readonly isReReviewLifecycle = computed(() => this.stage() !== null);
  readonly reviewNotes = this._reviewNotes.asReadonly();

  /**
   * Current lifecycle stage, or null when no re-review is due soon.
   * A change-based Custom Trigger (FR-7.5) always takes priority over the standard schedule.
   * An in-flight re-review application (draft/submitted/changes-requested) always takes
   * priority over the standard due-date schedule since it's already been actioned.
   */
  readonly stage = computed<KycReviewStage>(() => {
    const dto = this._statusDto();
    if (!dto) return null;
    if (dto.triggerReason) return 'changeRequired';
    const reviewStage = REVIEW_STATUS_STAGE[dto.kycReviewStatus ?? ''];
    if (reviewStage) return reviewStage;
    if (dto.isOverdue) return 'pastdue';
    if (dto.daysUntilDue <= WARNING_THRESHOLD_DAYS) return 'warning';
    if (dto.daysUntilDue <= DUE_THRESHOLD_DAYS) return 'due';
    if (dto.daysUntilDue <= UPCOMING_THRESHOLD_DAYS) return 'upcoming';
    return null;
  });

  constructor() {
    // Load once the company context is ready and accessible (mirrors company-permissions gating).
    effect(() => {
      const ready =
        this.onboardingStatus.isPortalAccessible() && this.onboardingStatus.status() === 'Active';
      if (ready && !this.hasLoaded) {
        this.hasLoaded = true;
        this.load();
      }
    });
  }

  load(): void {
    this.api.getKycReviewStatus(true).subscribe({
      next: (dto) => {
        this._statusDto.set(dto);
        this._statusLoaded.set(true);
        if (dto.kycReviewStatus === 'RevisionsRequested') {
          this.loadReviewNotes();
        } else {
          this._reviewNotes.set(null);
        }
      },
      error: () => {
        this._statusDto.set(null);
        this._statusLoaded.set(true);
        this._reviewNotes.set(null);
      },
    });
  }

  private loadReviewNotes(): void {
    this.api.getCurrentApplication(true).subscribe({
      next: (application) => this._reviewNotes.set(application.reviewNotes ?? null),
      error: () => this._reviewNotes.set(null),
    });
  }

  /**
   * Handles the FR-7 banner/notification "Start KYC Review" action — creates the
   * self-service re-review application (POST /api/onboarding/kyc-review/start) ahead
   * of/around the due date. Once an application exists (`reReviewDraft` /
   * `reReviewChangesRequested` stages), the banner instead links straight back into
   * the onboarding wizard — see `OnboardingShellComponent` re-review entry.
   */
  startReReview(): void {
    if (!this.onboardingStatus.canManageKycReview()) return;
    if (!this.canStartReReview()) {
      this.snackbar.info(this.transloco.translate('homepage.kyc.reviewStage.alreadyInProgress'));
      return;
    }
    this.api.startKycReReview().subscribe({
      next: () => {
        this.load();
        this.snackbar.success(this.transloco.translate('homepage.kyc.reviewStage.startSuccess'));
      },
      error: () => {
        this.snackbar.error(this.transloco.translate('homepage.kyc.reviewStage.startError'));
      },
    });
  }
}
